/**
 * Universal Referral Tracking Utility
 * 
 * This module provides centralized referral/attribution tracking that works
 * across ALL pages and entry points in the application.
 * 
 * Key principles:
 * 1. First referral wins - never overwrite existing referral data
 * 2. Works on ANY page with ref/utm params
 * 3. Persists across page reloads, navigation, and Stripe redirects
 * 4. localStorage is the source of truth until account creation
 */

// Storage keys
const STORAGE_KEYS = {
  REFERRAL_CODE: 'referral_code',
  UTM_SOURCE: 'utm_source',
  UTM_CAMPAIGN: 'utm_campaign',
  CAPTURED_AT: 'referral_captured_at'
};

/**
 * Capture referral and UTM parameters from the current URL.
 * 
 * CRITICAL: This function respects the "first referral wins" rule.
 * If a referral_code already exists in localStorage, it will NOT be overwritten.
 * 
 * @param {URLSearchParams|null} searchParams - URL search params (optional, will use window.location if not provided)
 * @returns {Object} - The captured or existing referral data
 */
export function captureReferralParams(searchParams = null) {
  // Get params from URL if not provided
  if (!searchParams) {
    searchParams = new URLSearchParams(window.location.search);
  }
  
  const urlRef = searchParams.get('ref');
  const urlUtmSource = searchParams.get('utm_source');
  const urlUtmCampaign = searchParams.get('utm_campaign');
  
  // Get existing values from localStorage
  const existingRef = localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE);
  const existingUtmSource = localStorage.getItem(STORAGE_KEYS.UTM_SOURCE);
  const existingUtmCampaign = localStorage.getItem(STORAGE_KEYS.UTM_CAMPAIGN);
  
  // FIRST REFERRAL WINS - Only store if NOT already present
  // This ensures we track the original source of the user
  
  if (urlRef && !existingRef) {
    localStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, urlRef);
    localStorage.setItem(STORAGE_KEYS.CAPTURED_AT, new Date().toISOString());
    console.log(`[Referral] Captured referral_code: ${urlRef}`);
  }
  
  if (urlUtmSource && !existingUtmSource) {
    localStorage.setItem(STORAGE_KEYS.UTM_SOURCE, urlUtmSource);
    console.log(`[Referral] Captured utm_source: ${urlUtmSource}`);
  }
  
  if (urlUtmCampaign && !existingUtmCampaign) {
    localStorage.setItem(STORAGE_KEYS.UTM_CAMPAIGN, urlUtmCampaign);
    console.log(`[Referral] Captured utm_campaign: ${urlUtmCampaign}`);
  }
  
  // Return current state (whether just captured or existing)
  return getReferralData();
}

/**
 * Get the current referral data from localStorage.
 * This should be used when creating accounts or Stripe sessions.
 * 
 * @returns {Object} - The referral data object
 */
export function getReferralData() {
  return {
    referral_code: localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE) || null,
    utm_source: localStorage.getItem(STORAGE_KEYS.UTM_SOURCE) || null,
    utm_campaign: localStorage.getItem(STORAGE_KEYS.UTM_CAMPAIGN) || null,
    captured_at: localStorage.getItem(STORAGE_KEYS.CAPTURED_AT) || null
  };
}

/**
 * Clear all referral data from localStorage.
 * Call this AFTER the data has been successfully saved to the database.
 * 
 * Note: We typically DON'T clear this after Stripe checkout because
 * the user might cancel and come back. Only clear after successful
 * account creation with the data saved.
 */
export function clearReferralData() {
  localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
  localStorage.removeItem(STORAGE_KEYS.UTM_SOURCE);
  localStorage.removeItem(STORAGE_KEYS.UTM_CAMPAIGN);
  localStorage.removeItem(STORAGE_KEYS.CAPTURED_AT);
  console.log('[Referral] Cleared referral data from localStorage');
}

/**
 * Check if any referral data exists.
 * 
 * @returns {boolean} - True if any referral/UTM data exists
 */
export function hasReferralData() {
  return !!(
    localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE) ||
    localStorage.getItem(STORAGE_KEYS.UTM_SOURCE) ||
    localStorage.getItem(STORAGE_KEYS.UTM_CAMPAIGN)
  );
}

/**
 * Format referral data for Stripe metadata.
 * Stripe metadata values must be strings and have key limits.
 * 
 * @returns {Object} - Object suitable for Stripe metadata
 */
export function getStripeMetadata() {
  const data = getReferralData();
  const metadata = {};
  
  if (data.referral_code) {
    metadata.ref = data.referral_code;
  }
  if (data.utm_source) {
    metadata.utm_source = data.utm_source;
  }
  if (data.utm_campaign) {
    metadata.utm_campaign = data.utm_campaign;
  }
  
  return metadata;
}

// Export storage keys for direct access if needed
export { STORAGE_KEYS };
